import { styled } from '@mui/material/styles';
import MuiTableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import type { Column, Table } from '@tanstack/react-table';
import { NumberValue } from '../../Number';
import { getCellAlign } from '../cell/align';
import { BodyCell } from '../components/BodyCell';
import { useDataProvider } from '../DataProviderContext';
import { getTableColumnDef } from '../internal/getTableColumnDef';
import { useAggregation } from './AggregationContext';
import type { AggregationFn, AggregationType } from './types';

const Cell = styled(BodyCell, { name: 'TableFooter', slot: 'Cell' })(
  ({ theme }) => ({
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.text.primary,
  })
);

export const FlatTableAggregations = () => {
  const { table, isLoading } = useDataProvider();
  const aggregation = useAggregation();

  if (!table || isLoading || aggregation === undefined) {
    return null;
  }

  const { aggregateFn, showTypes } = aggregation;
  const columns = table.getVisibleFlatColumns();
  const isEnabled = columns.some(
    (column) => column.columnDef.meta?.enableAggregations
  );

  if (!isEnabled && showTypes.length > 0) {
    return null;
  }

  return (
    <MuiTableFooter>
      {showTypes.map((type) => {
        return (
          <TableRow key={type}>
            {columns.map((column) => (
              <Cell key={column.id} align={getCellAlign(column)}>
                <Aggregation
                  type={type}
                  column={column}
                  table={table}
                  aggregateFn={aggregateFn}
                />
              </Cell>
            ))}
          </TableRow>
        );
      })}
    </MuiTableFooter>
  );
};

const Aggregation = ({
  type,
  column,
  table,
  aggregateFn,
}: {
  type: AggregationType;
  column: Column<any, any>;
  table: Table<any, any>;
  aggregateFn: AggregationFn<any>;
}) => {
  if (!column.columnDef.meta?.enableAggregations) {
    return null;
  }
  const value = aggregateFn(type, { column, table });
  const Component = getTableColumnDef(column).Aggregation;

  if (Component) {
    return <Component value={value} type={type} column={column} />;
  }

  const cellAlign = getCellAlign(column);

  return (
    <>
      {cellAlign === 'right' && (
        <>
          <AggregationType type={type} />{' '}
        </>
      )}
      <NumberValue
        value={value}
        fractionDigits={column.columnDef.meta?.fractionDigits ?? 1}
      />
      {cellAlign !== 'right' && (
        <>
          {' '}
          <AggregationType type={type} />
        </>
      )}
    </>
  );
};

const AggregationType = ({ type }: { type: string }) => (
  <>({type.toUpperCase()})</>
);
